<template>
  <div class="container mt-4">
    <!-- Title -->
    <h4 class="mb-4" style="color: #b03a45;">
      <i class="fas fa-folder-plus me-2"></i> {{ $t('documentType.title') }}
    </h4>

    <!-- Form Card -->
    <div class="card shadow border-0 p-4 mb-5" style="background-color: #ffffff; border-radius: 12px;">
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('documentType.name') }}</label>
              <input type="text" class="form-control" v-model="form.name" required :placeholder="$t('documentType.enterName')" />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label fw-bold text-dark">{{ $t('documentType.description') }}</label>
              <input type="text" class="form-control" v-model="form.description" :placeholder="$t('documentType.enterDescription')" />
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ isEdit ? $t('documentType.update') : $t('documentType.add') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('documentType.cancel') }}</button>
        </div>
      </form>
    </div>

    <!-- List Card -->
    <div class="card shadow border-0 p-4" style="background-color: #ffffff; border-radius: 12px;">
      <h5 class="mb-3 fw-bold" style="color: #b03a45;">
        <i class="fas fa-folder-open me-2"></i> {{ $t('documentType.existingDocumentTypes') }}
      </h5>

      <div class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('documentType.name') }}</th>
              <th>{{ $t('documentType.description') }}</th>
              <th class="text-end">{{ $t('documentType.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="type in documentTypes" :key="type.id">
              <td><strong>{{ type.name }}</strong></td>
              <td>{{ type.description || '-' }}</td>
              <td class="text-end">
                <button class="btn btn-sm text-white" style="background-color: #b03a45;" @click="editType(type)">
                  {{ $t('documentType.edit') }}
                </button>
                <button class="btn btn-sm btn-outline-danger ms-2" @click="deleteType(type.id)">
                  {{ $t('documentType.delete') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!documentTypes.length" class="text-muted mt-3">{{ $t('documentType.noDocumentTypes') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from '@/api'
import { useToast } from 'vue-toastification'

const toast = useToast()

const documentTypes = ref([])
const form = ref({
  name: '',
  description: ''
})
const isEdit = ref(false)
const currentId = ref(null)

// Fetch all document types
const fetchTypes = async () => {
  try {
    const res = await axios.get('/document-types')
    documentTypes.value = res.data || []
  } catch (err) {
    toast.error($t('documentType.failedToFetchDocumentTypes'))
    console.error(err)
  }
}

// Handle create / update
const handleSubmit = async () => {
  try {
    if (isEdit.value && currentId.value) {
      await axios.put(`/document-types/${currentId.value}`, form.value)
      toast.success($t('documentType.documentTypeUpdated'))
    } else {
      await axios.post('/document-types', form.value)
      toast.success($t('documentType.documentTypeAdded'))
    }
    await fetchTypes()
    resetForm()
  } catch (err) {
    toast.error($t('documentType.operationFailed'))
    console.error(err)
  }
}

// Load type in form for editing
const editType = (type) => {
  form.value = {
    name: type.name,
    description: type.description
  }
  currentId.value = type.id
  isEdit.value = true
}

// Delete document type
const deleteType = async (id) => {
  try {
    await axios.delete(`/document-types/${id}`)
    toast.success($t('documentType.documentTypeDeleted'))
    await fetchTypes()
  } catch (err) {
    toast.error($t('documentType.deleteFailed'))
    console.error(err)
  }
}

// Reset form
const resetForm = () => {
  form.value = { name: '', description: '' }
  isEdit.value = false
  currentId.value = null
}

onMounted(fetchTypes)
</script>
